import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import './LearningCertificationPage.css';
import API_BASE_URL from '../config';
import { isProducerLikeRole } from '../utils/talentPoolNavigation';

const skillCategories = [
  {
    name: 'Basics of Script Writing',
    subtitle: 'From Idea to Screen',
    whatYouWillLearn: [
      'Understanding storytelling fundamentals',
      'Difference between story, plot, and screenplay',
      'Writing for films, OTT, reels & digital content'
    ],
    keyTopics: [
      'Story structure (3-Act Structure)',
      'Character creation & arcs',
      'Scene writing & dialogue basics',
      'Visual writing (show, don\'t tell)',
      'Script formats (Short film, feature, digital)'
    ],
    outcomes: [
      'Write a basic short-film or digital script',
      'Develop a compelling logline and synopsis'
    ]
  },
  {
    name: 'Key Elements of Cinematography',
    subtitle: 'Visual Language of Cinema',
    whatYouWillLearn: [
      'How visuals communicate emotion and story',
      'Camera as a storytelling tool'
    ],
    keyTopics: [
      'Camera shots, angles & movements',
      'Composition & framing',
      'Lighting basics (natural & artificial)',
      'Lenses & focal lengths',
      'Mood, tone & visual continuity'
    ],
    outcomes: [
      'Plan shot lists & visual references',
      'Understand how to shoot cinematic content with available resources'
    ]
  },
  {
    name: 'Voice Over & Audio Basics',
    subtitle: 'Power of Voice & Sound',
    whatYouWillLearn: [
      'Importance of voice in storytelling & branding',
      'Basics of voice modulation and clarity'
    ],
    keyTopics: [
      'Voice control, pitch & emotion',
      'Script reading techniques',
      'Recording basics (mic, environment)',
      'Dubbing, narration & explainer voice overs'
    ],
    outcomes: [
      'Record a clean voice-over',
      'Understand professional VO workflows'
    ]
  },
  {
    name: 'Line Producer & Production Management',
    subtitle: 'Execution Behind the Scenes',
    whatYouWillLearn: [
      'How productions are planned, budgeted & executed',
      'Role of a Line Producer in films and digital content'
    ],
    keyTopics: [
      'Budgeting & scheduling basics',
      'Crew & equipment management',
      'Location, permissions & logistics',
      'Production workflows (Pre, Production, Post)',
      'Risk & crisis management'
    ],
    outcomes: [
      'Prepare a basic production plan',
      'Understand real-world production execution'
    ]
  },
  {
    name: 'Editing Fundamentals',
    subtitle: 'Shaping the Final Story',
    whatYouWillLearn: [
      'Role of editing in storytelling',
      'Editing rhythm, pace & emotion'
    ],
    keyTopics: [
      'Types of cuts & transitions',
      'Continuity editing basics',
      'Editing for films vs reels',
      'Introduction to editing software workflows'
    ],
    outcomes: [
      'Edit a basic short video',
      'Understand how editing enhances narrative'
    ]
  },
  {
    name: 'Colour Grading & Visual Enhancement',
    subtitle: 'Creating Mood & Consistency',
    whatYouWillLearn: [
      'Difference between color correction & color grading',
      'How colour impacts storytelling'
    ],
    keyTopics: [
      'Color theory basics',
      'Look creation & visual tone',
      'Matching shots for continuity',
      'Introduction to grading tools'
    ],
    outcomes: [
      'Apply basic colour grading',
      'Create a consistent visual look'
    ]
  },
  {
    name: 'Anchoring & On-Camera Presentation',
    subtitle: 'Confidence on Camera',
    whatYouWillLearn: [
      'On-camera communication skills',
      'Body language, expression & clarity'
    ],
    keyTopics: [
      'Scripted vs unscripted anchoring',
      'News, show hosting & digital formats',
      'Voice, posture & eye contact',
      'Handling live & recorded formats'
    ],
    outcomes: [
      'Present confidently on camera',
      'Anchor short-form & long-form content'
    ]
  },
  {
    name: 'Acting Basics',
    subtitle: 'Performance for Camera',
    whatYouWillLearn: [
      'Acting fundamentals for screen & digital media'
    ],
    keyTopics: [
      'Understanding emotions & expressions',
      'Body language & screen presence',
      'Acting for films vs digital formats',
      'Audition basics & camera tests'
    ],
    outcomes: [
      'Perform basic on-camera scenes',
      'Understand acting techniques for content creators'
    ]
  },
  {
    name: 'Social Media Influencer & Creator Economy',
    subtitle: 'Building Personal Brand & Monetization',
    whatYouWillLearn: [
      'How content creators build influence & income'
    ],
    keyTopics: [
      'Content formats (Reels, Shorts, YouTube)',
      'Audience psychology & engagement',
      'Personal branding & niche positioning',
      'Collaborations, monetization & ethics'
    ],
    outcomes: [
      'Build a basic creator strategy',
      'Understand influencer ecosystem & growth paths'
    ]
  },
  {
    name: 'Cinepreneur Mindset',
    subtitle: 'From Creator to Entrepreneur',
    whatYouWillLearn: [
      'Media as a business opportunity'
    ],
    keyTopics: [
      'Media startups & opportunities',
      'IP creation & ownership',
      'Funding, grants & incubator ecosystem',
      'Career pathways in M&E'
    ],
    outcomes: [
      'Think like a Cinepreneur',
      'Identify career & startup opportunities'
    ]
  }
];

const upskillCategories = [
  {
    name: 'Advanced Script Writing (Level 2)',
    subtitle: 'From Idea to Screen',
    whatYouWillLearn: [
      'Advanced storytelling techniques and narrative structures',
      'Mastering complex plot development and character arcs',
      'Professional writing for films, OTT series, reels & digital content'
    ],
    keyTopics: [
      'Advanced story structures (5-Act, Hero\'s Journey)',
      'Complex character development & multi-layered arcs',
      'Advanced scene writing & dialogue mastery',
      'Visual storytelling techniques (cinematic language)',
      'Professional script formats & industry standards'
    ],
    outcomes: [
      'Write professional feature-length scripts or series pilots',
      'Develop market-ready loglines, synopses, and pitch decks'
    ]
  },
  {
    name: 'Advanced Cinematography (Level 2)',
    subtitle: 'Visual Language of Cinema',
    whatYouWillLearn: [
      'Master visual storytelling and emotional communication',
      'Advanced camera techniques as narrative tools'
    ],
    keyTopics: [
      'Advanced camera movements & complex shot design',
      'Master composition & advanced framing techniques',
      'Professional lighting setups (studio & location)',
      'Lens selection & advanced focal length techniques',
      'Creating visual style & maintaining continuity across projects'
    ],
    outcomes: [
      'Create professional shot lists & storyboards',
      'Execute cinematic content with professional-grade resources'
    ]
  },
  {
    name: 'Advanced Voice Over & Audio Production (Level 2)',
    subtitle: 'Power of Voice & Sound',
    whatYouWillLearn: [
      'Master voice as a powerful storytelling & branding tool',
      'Advanced voice modulation, character work, and clarity'
    ],
    keyTopics: [
      'Advanced voice control, character voices & emotion',
      'Professional script interpretation & delivery',
      'Studio recording techniques & audio engineering',
      'Commercial narration, character dubbing & explainer VO mastery'
    ],
    outcomes: [
      'Record professional-grade voice-overs',
      'Master professional VO workflows & industry standards'
    ]
  },
  {
    name: 'Advanced Line Producer & Production Management (Level 2)',
    subtitle: 'Execution Behind the Scenes',
    whatYouWillLearn: [
      'Master production planning, budgeting & execution',
      'Advanced role of Line Producer in films and digital content'
    ],
    keyTopics: [
      'Advanced budgeting & financial management',
      'Complex scheduling & multi-project coordination',
      'Crew management & talent relations',
      'Advanced location scouting, permissions & logistics',
      'Production workflows optimization & crisis management'
    ],
    outcomes: [
      'Create comprehensive production plans for large-scale projects',
      'Master real-world production execution & team leadership'
    ]
  },
  {
    name: 'Advanced Editing (Level 2)',
    subtitle: 'Shaping the Final Story',
    whatYouWillLearn: [
      'Master editing as the final rewrite of storytelling',
      'Advanced editing rhythm, pace, emotion & narrative structure'
    ],
    keyTopics: [
      'Advanced cuts, transitions & editing techniques',
      'Master continuity editing & invisible cuts',
      'Professional editing for films, series & digital platforms',
      'Advanced editing software workflows & collaboration'
    ],
    outcomes: [
      'Edit professional short films and digital content',
      'Master how advanced editing transforms narrative'
    ]
  },
  {
    name: 'Advanced Colour Grading & Visual Enhancement (Level 2)',
    subtitle: 'Creating Mood & Consistency',
    whatYouWillLearn: [
      'Master the art of color correction & color grading',
      'Advanced understanding of how colour shapes storytelling'
    ],
    keyTopics: [
      'Advanced color theory & color psychology',
      'Professional look creation & visual tone mastery',
      'Advanced shot matching & continuity',
      'Professional grading tools & workflows (DaVinci Resolve, etc.)'
    ],
    outcomes: [
      'Apply professional-grade colour grading',
      'Create distinctive visual looks and maintain consistency'
    ]
  },
  {
    name: 'Advanced Anchoring & On-Camera Presentation (Level 2)',
    subtitle: 'Confidence on Camera',
    whatYouWillLearn: [
      'Master on-camera communication and presentation skills',
      'Advanced body language, expression & clarity techniques'
    ],
    keyTopics: [
      'Advanced scripted & unscripted anchoring techniques',
      'Professional news, show hosting & digital format mastery',
      'Advanced voice, posture & eye contact techniques',
      'Mastering live broadcasts & recorded formats'
    ],
    outcomes: [
      'Present with professional confidence on camera',
      'Master anchoring for all short-form & long-form content types'
    ]
  },
  {
    name: 'Advanced Acting (Level 2)',
    subtitle: 'Performance for Camera',
    whatYouWillLearn: [
      'Master acting techniques for screen & digital media'
    ],
    keyTopics: [
      'Advanced emotional range & expression techniques',
      'Professional body language & screen presence',
      'Master acting for films, series & digital formats',
      'Professional audition techniques & camera test mastery'
    ],
    outcomes: [
      'Perform professional on-camera scenes',
      'Master acting techniques for content creators & professionals'
    ]
  },
  {
    name: 'Advanced Social Media Influencer & Creator Economy (Level 2)',
    subtitle: 'Building Personal Brand & Monetization',
    whatYouWillLearn: [
      'Master how content creators build influence & sustainable income'
    ],
    keyTopics: [
      'Advanced content formats & platform optimization',
      'Master audience psychology & engagement strategies',
      'Professional personal branding & niche dominance',
      'Advanced collaborations, monetization strategies & ethical practices'
    ],
    outcomes: [
      'Build comprehensive creator strategies',
      'Master influencer ecosystem & sustainable growth paths'
    ]
  },
  {
    name: 'Advanced Cinepreneur Mindset (Level 2)',
    subtitle: 'From Creator to Entrepreneur',
    whatYouWillLearn: [
      'Master media as a business opportunity and entrepreneurial venture'
    ],
    keyTopics: [
      'Advanced media startup strategies & opportunities',
      'IP creation, ownership & monetization',
      'Advanced funding strategies, grants & incubator ecosystem',
      'Professional career pathways & leadership in M&E'
    ],
    outcomes: [
      'Think and operate as a professional Cinepreneur',
      'Identify and execute career & startup opportunities'
    ]
  }
];

const LearningCertificationPage = () => {
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const showDashboardParam = searchParams.get('showDashboard');
  const isCertifiedProfessionalRoute = pathname === '/certified-professional';
  const [activeSection, setActiveSection] = useState(
    sectionParam && ['skill', 'upskill', 'reskill'].includes(sectionParam) 
      ? sectionParam 
      : 'skill'
  ); // 'skill', 'upskill', 'reskill'
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [message, setMessage] = useState(null); // custom message modal (no browser "says" prefix)
  const [showDashboard, setShowDashboard] = useState(
    showDashboardParam === 'true' || isCertifiedProfessionalRoute
  );

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('learningCart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (err) {
        console.error('Error loading cart from localStorage:', err);
      }
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('learningCart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = (module, section) => {
    const cartItem = {
      id: `${section}-${module.name}`,
      name: module.name,
      subtitle: module.subtitle,
      section: section,
      price: 1799,
      originalPrice: 1999
    };
    
    // Check if already in cart
    if (!cart.find(item => item.id === cartItem.id)) {
      setCart([...cart, cartItem]);
      setMessage(`${module.name} added to cart!`);
    } else {
      setMessage(`${module.name} is already in your cart!`);
    }
  };

  const removeFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + item.price, 0);
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => resolve(new Error('Razorpay script failed to load'));
      document.body.appendChild(script);
    });
  };

  const handleCheckout = async () => {
    const total = getTotalPrice();
      if (total <= 0) {
      setMessage('Cart is empty');
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/api/razorpay/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: total })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const parts = [data.error || 'Failed to create order'];
        if (data.details) parts.push(data.details);
        setMessage(parts.join('\n'));
        return;
      }
      const { orderId, keyId } = data;
      await loadRazorpayScript();
      if (!window.Razorpay) {
        setMessage('Payment gateway failed to load. Try again.');
        return;
      }
      const options = {
        key: keyId,
        amount: data.amount,
        currency: data.currency || 'INR',
        order_id: orderId,
        name: "Creator's Arch",
        description: 'Learning & Certification',
        handler: async function (payload) {
          try {
            const verifyRes = await fetch(`${API_BASE_URL}/api/razorpay/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                orderId: payload.razorpay_order_id,
                paymentId: payload.razorpay_payment_id,
                signature: payload.razorpay_signature
              })
            });
            const verifyData = await verifyRes.json().catch(() => ({}));
            if (verifyData.verified) {
              setCart([]);
              setShowCart(false);
              setMessage('Payment successful! Thank you for your purchase.');
            } else {
              setMessage('Payment verification failed. Contact support if amount was deducted.');
            }
          } catch (err) {
            setMessage('Verification failed. Contact support if amount was deducted.');
          }
        },
        prefill: {
          email: user?.email || '',
          contact: user?.contact || user?.phone || user?.mobile || ''
        },
        theme: { color: '#007bff' }
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', () => {
        setMessage('Payment failed or was cancelled.');
      });
      rzp.open();
    } catch (err) {
      console.error(err);
      setMessage('Checkout failed. Please try again.');
    }
  };

  const [certifiedStudents, setCertifiedStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Filter states
  const [searchName, setSearchName] = useState('');
  const [filterDesignation, setFilterDesignation] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterExperience, setFilterExperience] = useState('');

  // Producer view mode: 1 = names/details hidden, 2 = names/details visible
  const [producerViewMode, setProducerViewMode] = useState(1);
  const [trailerTitle, setTrailerTitle] = useState('');
  const [trailerFile, setTrailerFile] = useState(null);
  const [trailerVideos, setTrailerVideos] = useState([]);

  // Contact stats for producers (used to compute trending skill/city)
  const [contactStats, setContactStats] = useState({
    totalContacts: 0,
    skillCounts: {},
    cityCounts: {}
  });

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');

    if (!token || !storedUser) {
      setMessage('Please login to access Learning & Certification');
      navigate('/login', { state: { from: '/learning-certification' } });
      return;
    }

    try {
      const userData = JSON.parse(storedUser);
      setUser(userData);

      // For any non-talent user (e.g., producer, recruiter), load dummy data
      if (userData.userType !== 'talent') {
        loadDummyCertifiedProfiles();
      }
    } catch (err) {
      console.error('Error parsing user data:', err);
      navigate('/login');
    }
  }, [navigate]);

  // When on Certified Professional route as producer, show dashboard by default
  useEffect(() => {
    if (isCertifiedProfessionalRoute && user && user.userType !== 'talent') {
      setShowDashboard(true);
    }
  }, [isCertifiedProfessionalRoute, user]);

  // Load saved contact stats (for producers) from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('producerContactStats');
    if (!saved) return;

    try {
      const parsed = JSON.parse(saved);
      setContactStats({
        totalContacts: parsed.totalContacts || 0,
        skillCounts: parsed.skillCounts || {},
        cityCounts: parsed.cityCounts || {}
      });
    } catch (err) {
      console.error('Error loading producer contact stats:', err);
    }
  }, []);

  // Persist contact stats so trending values survive page refresh
  useEffect(() => {
    localStorage.setItem('producerContactStats', JSON.stringify(contactStats));
  }, [contactStats]);

  const fetchCertificationTrailers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/certification-trailers`);
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setTrailerVideos(data.trailers || []);
      } else {
        console.error('Failed to fetch certification trailers:', data.error);
      }
    } catch (err) {
      console.error('Error fetching certification trailers:', err);
    }
  };

  useEffect(() => {
    fetchCertificationTrailers();
  }, []);

  const fetchCertifiedStudents = async () => {
    try {
      setLoadingStudents(true);
      const res = await fetch(`${API_BASE_URL}/api/profiles`);
      if (res.ok) {
        const data = await res.json();
        setCertifiedStudents(data.profiles || []);
      } else {
        console.error('Failed to fetch certified students');
      }
    } catch (err) {
      console.error('Error fetching certified students:', err);
    } finally {
      setLoadingStudents(false);
    }
  };

  // Load dummy certified profiles for producers
  const loadDummyCertifiedProfiles = () => {
    const dummyProfiles = [
      {
        Id: 1,
        Name: 'Rajesh Kumar',
        UserName: 'rajesh_k',
        Designation: 'Video Editor',
        Skill: 'Editing, Color Grading',
        CityLocation: 'Mumbai, Maharashtra',
        Experience: '3-5 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 2,
        Name: 'Priya Sharma',
        UserName: 'priya_s',
        Designation: 'Cinematographer',
        Skill: 'Cinematography, Lighting',
        CityLocation: 'Delhi, NCR',
        Experience: '5-7 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 3,
        Name: 'Amit Patel',
        UserName: 'amit_p',
        Designation: 'Script Writer',
        Skill: 'Script Writing, Storytelling',
        CityLocation: 'Bangalore, Karnataka',
        Experience: '1-3 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 4,
        Name: 'Sneha Reddy',
        UserName: 'sneha_r',
        Designation: 'Voice Over Artist',
        Skill: 'Voice Over, Dubbing',
        CityLocation: 'Hyderabad, Telangana',
        Experience: '2-4 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 5,
        Name: 'Vikram Singh',
        UserName: 'vikram_s',
        Designation: 'Production Manager',
        Skill: 'Production Management, Scheduling',
        CityLocation: 'Pune, Maharashtra',
        Experience: '7-10 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 6,
        Name: 'Anjali Mehta',
        UserName: 'anjali_m',
        Designation: 'Sound Designer',
        Skill: 'Sound Design, Audio Mixing',
        CityLocation: 'Chennai, Tamil Nadu',
        Experience: '3-5 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 7,
        Name: 'Rohit Verma',
        UserName: 'rohit_v',
        Designation: 'VFX Artist',
        Skill: 'VFX, Compositing',
        CityLocation: 'Mumbai, Maharashtra',
        Experience: '5-7 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      },
      {
        Id: 8,
        Name: 'Kavita Nair',
        UserName: 'kavita_n',
        Designation: 'Anchor & Presenter',
        Skill: 'Anchoring, On-Camera Presentation',
        CityLocation: 'Bangalore, Karnataka',
        Experience: '1-3 years',
        ProfileImageUrl: null,
        ResumeUrl: '#'
      }
    ];
    setCertifiedStudents(dummyProfiles);
  };

  // Filter students based on search and filter criteria
  const filteredStudents = certifiedStudents.filter(student => {
    const matchesName = !searchName || 
      (student.Name && student.Name.toLowerCase().includes(searchName.toLowerCase())) ||
      (student.UserName && student.UserName.toLowerCase().includes(searchName.toLowerCase()));
    
    const matchesDesignation = !filterDesignation || 
      (student.Designation && student.Designation.toLowerCase().includes(filterDesignation.toLowerCase()));
    
    const matchesSkill = !filterSkill || 
      (student.Skill && student.Skill.toLowerCase().includes(filterSkill.toLowerCase()));
    
    const matchesLocation = !filterLocation || 
      (student.CityLocation && student.CityLocation.toLowerCase().includes(filterLocation.toLowerCase()));

    const matchesExperience = !filterExperience ||
      (student.Experience && student.Experience.toLowerCase() === filterExperience.toLowerCase());
    
    return matchesName && matchesDesignation && matchesSkill && matchesLocation && matchesExperience;
  });

  // Get unique values for filter dropdowns
  const uniqueDesignations = [...new Set(certifiedStudents
    .map(s => s.Designation)
    .filter(d => d && d.trim() !== ''))].sort();
  
  const uniqueSkills = [...new Set(certifiedStudents
    .flatMap(s => s.Skill ? s.Skill.split(',').map(skill => skill.trim()) : [])
    .filter(skill => skill && skill.trim() !== ''))].sort();
  
  const uniqueLocations = [...new Set(certifiedStudents
    .map(s => s.CityLocation)
    .filter(l => l && l.trim() !== ''))].sort();

  const uniqueExperiences = [...new Set(certifiedStudents
    .map(s => s.Experience)
    .filter(e => e && e.trim() !== ''))].sort();

  useEffect(() => {
    // Update active section when URL parameter changes
    if (sectionParam && ['skill', 'upskill', 'reskill'].includes(sectionParam)) {
      setActiveSection(sectionParam);
    }
  }, [sectionParam]);

  // Close cart when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCart && !event.target.closest('.cart-container')) {
        setShowCart(false);
      }
    };

    if (showCart) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showCart]);

  // Helper to find the key with maximum count in a map
  const getTrendingKey = (counts) => {
    const entries = Object.entries(counts || {});
    if (!entries.length) return '-';
    entries.sort((a, b) => b[1] - a[1]);
    return entries[0][0];
  };

  const trendingSkill = getTrendingKey(contactStats.skillCounts);
  const trendingCity = getTrendingKey(contactStats.cityCounts);

  const handleContactTalent = (student) => {
    if (!student) return;

    const skillRaw = student.Skill || '';
    const cityRaw = student.CityLocation || '';
    const skill = skillRaw.trim() || 'Unknown';
    const city = cityRaw.trim() || 'Unknown';

    setContactStats(prev => {
      const nextSkillCounts = { ...prev.skillCounts };
      const nextCityCounts = { ...prev.cityCounts };

      nextSkillCounts[skill] = (nextSkillCounts[skill] || 0) + 1;
      nextCityCounts[city] = (nextCityCounts[city] || 0) + 1;

      return {
        totalContacts: prev.totalContacts + 1,
        skillCounts: nextSkillCounts,
        cityCounts: nextCityCounts
      };
    });

    setMessage(`Contact recorded for ${student.Name || student.UserName || 'this talent'}.`);
  };

  const handleUploadTrailer = async (e) => {
    e.preventDefault();
    const title = trailerTitle.trim();
    if (!title || !trailerFile) {
      setMessage('Please enter trailer title and choose an MP4 video.');
      return;
    }
    if (!/\.mp4$/i.test(trailerFile.name || '')) {
      setMessage('Only .mp4 files are supported.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please login to upload trailer videos.');
        return;
      }

      const formData = new FormData();
      formData.append('title', title);
      formData.append('video', trailerFile);

      const res = await fetch(`${API_BASE_URL}/api/certification-trailers/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMessage(data.error || 'Failed to upload trailer');
        return;
      }

      setTrailerTitle('');
      setTrailerFile(null);
      await fetchCertificationTrailers();
      setMessage('Trailer uploaded to storage account successfully.');
    } catch (err) {
      console.error('Trailer upload failed:', err);
      setMessage('Trailer upload failed.');
    }
  };

  const handleRemoveTrailer = async (id) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setMessage('Please login to remove trailers.');
        return;
      }
      const res = await fetch(`${API_BASE_URL}/api/certification-trailers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMessage(data.error || 'Failed to remove trailer.');
        return;
      }
      await fetchCertificationTrailers();
    } catch (err) {
      console.error('Failed to remove trailer:', err);
      setMessage('Failed to remove trailer.');
    }
  };

  return (
    <div className="learning-container">
      {/* Header */}
      <header className="marketplace-header">
        <div className="logo-left">
          <img src="/p3.jpg" alt="Cinepreneur Logo" className="cinepreneur-logo" />
        </div>
        <div className="header-content">
          <h1 className="marketplace-title">CREATOR'S ARCH</h1>
          <p className="marketplace-subtitle">EMPOWERING CREATORS, PROFESSIONALS</p>
        </div>
        <div className="logo-right">
          <img src="/p1.jpg" alt="MeeSchool Logo" className="meeschool-logo" />
          {user && (user.userType === 'talent' || pathname === '/learning-certification') && (
          <div className="cart-container">
            <button className="cart-icon-button" onClick={() => setShowCart(!showCart)}>
              <span className="cart-icon">🛒</span>
              {cart.length > 0 && <span className="cart-badge">{cart.length}</span>}
            </button>
            {showCart && (
              <div className="cart-dropdown">
                <div className="cart-header">
                  <h3>Shopping Cart ({cart.length})</h3>
                  <button className="close-cart" onClick={() => setShowCart(false)}>×</button>
                </div>
                {cart.length === 0 ? (
                  <div className="cart-empty">
                    <p>Your cart is empty</p>
                  </div>
                ) : (
                  <>
                    <div className="cart-items">
                      {cart.map((item) => (
                        <div key={item.id} className="cart-item">
                          <div className="cart-item-info">
                            <h4>{item.name}</h4>
                            <p className="cart-item-subtitle">{item.subtitle}</p>
                            <p className="cart-item-section">{item.section === 'skill' ? 'Level 1' : 'Level 2'}</p>
                            <p className="cart-item-price">₹{item.price}</p>
                          </div>
                          <button className="remove-item" onClick={() => removeFromCart(item.id)}>
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="cart-footer">
                      <div className="cart-total">
                        <span>Total: ₹{getTotalPrice()}</span>
                      </div>
                      <button className="checkout-button" onClick={handleCheckout}>
                        Proceed to Checkout
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
          )}
        </div>
      </header>

      <div className="marketplace-body">
        {/* Left Navigation Sidebar */}
        <nav className="sidebar-nav">
          <div className="nav-item" onClick={() => navigate('/')}>HOME</div>
          {user && user.userType !== 'talent' ? (
            <>
              <div
                className={`nav-item ${(pathname === '/certified-professional' || (pathname === '/learning-certification' && showDashboard)) ? 'active' : ''}`}
                onClick={() => navigate('/certified-professional')}
              >
                CERTIFIED PROFESSIONAL
              </div>
              {isProducerLikeRole(user.userType) && (
                <div className="nav-item" onClick={() => navigate('/profiles')}>ALL PROFILES</div>
              )}
            </>
          ) : (
            <>
              <div
                className={`nav-item ${pathname === '/learning-certification' ? 'active' : ''}`}
                onClick={() => navigate('/learning-certification')}
              >
                LEARNING / CERTIFICATION
              </div>
              <div className="nav-item" onClick={() => navigate('/profile')}>PROFILING</div>
            </>
          )}
          <div className="nav-item">
            SOURCING
            <div className="nav-subitem">TALENT/EQUIPMENT</div>
          </div>
          <div className="nav-item" onClick={() => navigate(user && user.userType !== 'talent' ? '/producer-pitches' : '/pitch-upload')}>PITCHING</div>
          <div className="nav-item">
            VALIDATE
            <div className="nav-subitem">PROOF OF CONCEPTS</div>
          </div>
          <div className="nav-item" onClick={() => navigate('/funding-projects')}>FUNDING ZONE</div>
        </nav>

        {/* Main Content Area */}
        <main className="main-content">
          <h2 className="page-title">
            {user && user.userType !== 'talent' && (showDashboard || isCertifiedProfessionalRoute)
              ? 'Certified Professional'
              : 'Learning & Certification'}
          </h2>
          
          {/* Certified Professional dashboard: only when producer/recruiter is on that section */}
          {user && user.userType !== 'talent' && (isCertifiedProfessionalRoute || showDashboard) ? (
            !showDashboard && isCertifiedProfessionalRoute ? (
              <div className="explore-profiles-container">
                <div className="explore-profiles-box">
                  <h3>Certified Talent Available</h3>
                  <p>Browse through certified students and professionals ready for job opportunities.</p>
                  <button 
                    className="explore-profiles-btn"
                    onClick={() => setShowDashboard(true)}
                  >
                    Explore Profiles
                  </button>
                </div>
              </div>
            ) : (
              <div className="dashboard-container">
                <div className="dashboard-box">
                  <div className="dashboard-header">
                    <div>
                      <h3>Students Available for Jobs</h3>
                      <span className="student-count">{filteredStudents.length} {filteredStudents.length === 1 ? 'candidate' : 'candidates'}</span>
                    </div>
                    <div className="dashboard-header-actions">
                      <div className="producer-mode-selector">
                        <span className="mode-label">View:</span>
                        <button
                          type="button"
                          className={`mode-btn ${producerViewMode === 1 ? 'active' : ''}`}
                          onClick={() => setProducerViewMode(1)}
                        >
                          Free
                        </button>
                        <button
                          type="button"
                          className={`mode-btn ${producerViewMode === 2 ? 'active' : ''}`}
                          onClick={() => setProducerViewMode(2)}
                        >
                          Subscribed
                        </button>
                        <span className="mode-hint">
                          {producerViewMode === 1 ? 'Names & details hidden' : 'Names & details visible'}
                        </span>
                      </div>
                      <button 
                        className="back-to-explore-btn"
                        onClick={() => setShowDashboard(false)}
                      >
                        ← Back
                      </button>
                    </div>
                  </div>

                  {/* Stats row for producers */}
                  <div className="stats-row">
                    <div className="stat-card stat-total">
                      <div className="stat-label">TOTAL TALENT</div>
                      <div className="stat-value">{certifiedStudents.length}</div>
                    </div>
                    <div className="stat-card stat-skill">
                      <div className="stat-label">TRENDING SKILL</div>
                      <div className="stat-value">{trendingSkill}</div>
                    </div>
                    <div className="stat-card stat-city">
                      <div className="stat-label">TRENDING CITY</div>
                      <div className="stat-value">{trendingCity}</div>
                    </div>
                    <div className="stat-card stat-contacts">
                      <div className="stat-label">CONTACTS</div>
                      <div className="stat-value">{contactStats.totalContacts}</div>
                    </div>
                    <button
                      type="button"
                      className="stat-reset-btn"
                      onClick={() => {
                        setContactStats({ totalContacts: 0, skillCounts: {}, cityCounts: {} });
                        localStorage.removeItem('producerContactStats');
                      }}
                    >
                      Reset
                    </button>
                  </div>
                  
                  {/* Filters Section */}
                  {certifiedStudents.length > 0 && (
                    <div className="dashboard-filters">
                      <div className="filter-row">
                        <div className="filter-group">
                          <label htmlFor="search-name">Search by Name</label>
                          <input
                            type="text"
                            id="search-name"
                            className="filter-input"
                            placeholder="Enter name..."
                            value={searchName}
                            onChange={(e) => setSearchName(e.target.value)}
                          />
                        </div>
                        <div className="filter-group">
                          <label htmlFor="filter-designation">Designation</label>
                          <select
                            id="filter-designation"
                            className="filter-select"
                            value={filterDesignation}
                            onChange={(e) => setFilterDesignation(e.target.value)}
                          >
                            <option value="">All Designations</option>
                            {uniqueDesignations.map(designation => (
                              <option key={designation} value={designation}>{designation}</option>
                            ))}
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="filter-skill">Skill</label>
                          <select
                            id="filter-skill"
                            className="filter-select"
                            value={filterSkill}
                            onChange={(e) => setFilterSkill(e.target.value)}
                          >
                            <option value="">All Skills</option>
                            {uniqueSkills.map(skill => (
                              <option key={skill} value={skill}>{skill}</option>
                            ))}
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="filter-location">Location</label>
                          <select
                            id="filter-location"
                            className="filter-select"
                            value={filterLocation}
                            onChange={(e) => setFilterLocation(e.target.value)}
                          >
                            <option value="">All Locations</option>
                            {uniqueLocations.map(location => (
                              <option key={location} value={location}>{location}</option>
                            ))}
                          </select>
                        </div>
                        <div className="filter-group">
                          <label htmlFor="filter-experience">Experience</label>
                          <select
                            id="filter-experience"
                            className="filter-select"
                            value={filterExperience}
                            onChange={(e) => setFilterExperience(e.target.value)}
                          >
                            <option value="">All Experience</option>
                            {uniqueExperiences.map(exp => (
                              <option key={exp} value={exp}>{exp}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          className="clear-filters-btn"
                          onClick={() => {
                            setSearchName('');
                            setFilterDesignation('');
                            setFilterSkill('');
                            setFilterLocation('');
                            setFilterExperience('');
                          }}
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>
                  )}

                  {loadingStudents ? (
                    <div className="dashboard-loading">
                      <p>Loading candidates...</p>
                    </div>
                  ) : certifiedStudents.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No candidates available at the moment.</p>
                    </div>
                  ) : filteredStudents.length === 0 ? (
                    <div className="dashboard-empty">
                      <p>No candidates match your filter criteria.</p>
                    </div>
                  ) : (
                    <div className="students-list">
                      {filteredStudents.map((student, index) => (
                        <div key={student.Id || index} className="student-list-item">
                          <div className="student-avatar">
                            {producerViewMode === 2 && student.ProfileImageUrl ? (
                              <img 
                                src={student.ProfileImageUrl} 
                                alt={student.Name || 'Profile'} 
                                className="student-avatar-img"
                              />
                            ) : (
                              <div className="student-avatar-placeholder">
                                {producerViewMode === 2 && student.Name
                                  ? student.Name.charAt(0).toUpperCase()
                                  : '?'}
                              </div>
                            )}
                          </div>
                          <div className="student-details">
                            {producerViewMode === 1 ? (
                              <>
                                <h4 className="student-name student-name-hidden">Candidate {index + 1}</h4>
                                <p className="student-designation student-detail-hidden">—</p>
                                <div className="student-meta student-detail-hidden">
                                  <span>—</span>
                                </div>
                              </>
                            ) : (
                              <>
                                <h4 className="student-name">{student.Name || student.UserName || 'N/A'}</h4>
                                {student.Designation && <p className="student-designation">{student.Designation}</p>}
                                <div className="student-meta">
                                  {student.Skill && <span className="student-skill">🎯 {student.Skill}</span>}
                                  {student.CityLocation && <span className="student-location">📍 {student.CityLocation}</span>}
                                  {student.Experience && <span className="student-experience">💼 {student.Experience}</span>}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="student-actions">
                            {producerViewMode === 2 && (
                              <button
                                type="button"
                                className="contact-btn"
                                onClick={() => handleContactTalent(student)}
                              >
                                Contact
                              </button>
                            )}
                            {producerViewMode === 2 && student.ResumeUrl && (
                              <a
                                href={student.ResumeUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="view-resume-btn"
                              >
                                📄 View Resume
                              </a>
                            )}
                            {producerViewMode === 1 && (
                              <span className="view-resume-locked">Switch to Subscribed to view</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )
          ) : (
            <>
              <section className="cert-trailers-section">
                <div className="cert-trailers-header">
                  <h3>Certification Trailer Videos</h3>
                  <p>Keep your module trailer videos here for quick access.</p>
                </div>

                <form className="cert-trailer-form" onSubmit={handleUploadTrailer}>
                  <input
                    type="text"
                    className="cert-trailer-input"
                    placeholder="Trailer title (e.g. Script Writing Intro)"
                    value={trailerTitle}
                    onChange={(e) => setTrailerTitle(e.target.value)}
                  />
                  <input
                    type="file"
                    accept=".mp4,video/mp4"
                    className="cert-trailer-input"
                    onChange={(e) => setTrailerFile(e.target.files?.[0] || null)}
                  />
                  <button type="submit" className="cert-trailer-add-btn">Upload to Storage</button>
                </form>

                {trailerVideos.length === 0 ? (
                  <div className="cert-trailer-empty">No trailer videos added yet.</div>
                ) : (
                  <div className="cert-trailer-grid">
                    {trailerVideos.map((video) => (
                      <article key={video.id} className="cert-trailer-card">
                        <h4>{video.title}</h4>
                        <div className="cert-trailer-frame-wrap">
                          <video
                            src={video.url}
                            title={video.title}
                            className="cert-trailer-frame"
                            controls
                            preload="metadata"
                          />
                        </div>
                        <div className="cert-trailer-actions">
                          <a href={video.url} target="_blank" rel="noopener noreferrer">
                            Open source
                          </a>
                          <button type="button" onClick={() => handleRemoveTrailer(video.id)}>
                            Remove
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              {/* Section Tabs - Skill / Upskill / Reskill (talent and producers) */}
              <div className="section-tabs">
                <button
                  className={`tab-button ${activeSection === 'skill' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSection('skill');
                    navigate('/learning-certification?section=skill', { replace: true });
                  }}
                >
                  🧩 SKILL (Level 1)
                </button>
                <button
                  className={`tab-button ${activeSection === 'upskill' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSection('upskill');
                    navigate('/learning-certification?section=upskill', { replace: true });
                  }}
                >
                  🚀 UPSKILL (Level 2)
                </button>
                <button
                  className={`tab-button ${activeSection === 'reskill' ? 'active' : ''}`}
                  onClick={() => {
                    setActiveSection('reskill');
                    navigate('/learning-certification?section=reskill', { replace: true });
                  }}
                >
                  📚 RESKILL
                </button>
                <button
                  className="tab-button"
                  onClick={() => {
                    window.open('https://icy-water-001285d0f.1.azurestaticapps.net/', '_blank', 'noopener,noreferrer');
                  }}
                >
                  🆕 LMS
                </button>
              </div>

          {/* Skill Section */}
          {activeSection === 'skill' && (
            <div className="section-content">
              <div className="section-header">
                <h3>🧩 SKILL SECTION (Level 1)</h3>
                <p>These are entry-level / core skills a creator selects during onboarding.</p>
              </div>
              <div className="categories-grid">
                {skillCategories.map((category, index) => (
                  <div key={index} className="category-card">
                    <h4 className="category-title">{category.name}</h4>
                    <p className="category-subtitle">{category.subtitle || '\u00A0'}</p>
                    <div className="module-pricing">
                      <div className="price-container">
                        <span className="original-price">₹1,999</span>
                      </div>
                      <div className="discounted-price">₹1,799</div>
                    </div>
                    <button 
                      className="add-to-cart-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(category, 'skill');
                      }}
                      disabled={cart.some(item => item.id === `skill-${category.name}`)}
                    >
                      {cart.some(item => item.id === `skill-${category.name}`) ? '✓ Added to Cart' : 'Add to Cart'}
                    </button>
                    {category.whatYouWillLearn && (
                      <div className="module-section">
                        <h5 className="module-section-title">What You Will Learn</h5>
                        <ul className="category-items">
                          {category.whatYouWillLearn.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {category.keyTopics && (
                      <div className="module-section hover-only">
                        <h5 className="module-section-title">Key Topics</h5>
                        <ul className="category-items">
                          {category.keyTopics.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {category.outcomes && (
                      <div className="module-section hover-only">
                        <h5 className="module-section-title">Outcomes</h5>
                        <ul className="category-items">
                          {category.outcomes.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upskill Section */}
          {activeSection === 'upskill' && (
            <div className="section-content">
              <div className="section-header">
                <h3>🚀 UPSKILL SECTION (Level 2)</h3>
                <p>These unlock after completing Skill level and qualify creators for higher-paying projects.</p>
              </div>
              <div className="categories-grid">
                {upskillCategories.map((category, index) => (
                  <div key={index} className="category-card">
                    <h4 className="category-title">{category.name}</h4>
                    <p className="category-subtitle">{category.subtitle || '\u00A0'}</p>
                    <div className="module-pricing">
                      <div className="price-container">
                        <span className="original-price">₹1,999</span>
                      </div>
                      <div className="discounted-price">₹1,799</div>
                    </div>
                    <button 
                      className="add-to-cart-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        addToCart(category, 'upskill');
                      }}
                      disabled={cart.some(item => item.id === `upskill-${category.name}`)}
                    >
                      {cart.some(item => item.id === `upskill-${category.name}`) ? '✓ Added to Cart' : 'Add to Cart'}
                    </button>
                    {category.whatYouWillLearn && (
                      <div className="module-section">
                        <h5 className="module-section-title">What You Will Learn</h5>
                        <ul className="category-items">
                          {category.whatYouWillLearn.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {category.keyTopics && (
                      <div className="module-section hover-only">
                        <h5 className="module-section-title">Key Topics</h5>
                        <ul className="category-items">
                          {category.keyTopics.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {category.outcomes && (
                      <div className="module-section hover-only">
                        <h5 className="module-section-title">Outcomes</h5>
                        <ul className="category-items">
                          {category.outcomes.map((item, itemIndex) => (
                            <li key={itemIndex}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reskill Section */}
          {activeSection === 'reskill' && (
            <div className="section-content">
              <div className="section-header">
                <h3>📚 RESKILL SECTION</h3>
                <p>Recordings for previous classes</p>
              </div>
              <div className="reskill-content">
                <div className="empty-state">
                  <p>📹 Class recordings will be available here</p>
                  <p className="sub-text">Previous class recordings and materials will appear in this section.</p>
                </div>
              </div>
            </div>
          )}
            </>
          )}
        </main>
      </div>

      {/* Footer */}
      <footer className="marketplace-footer">
        <p>© MEESCHOOL, 2026</p>
      </footer>

      {/* Custom message modal (no browser "says" prefix) */}
      {message != null && (
        <div className="learning-message-overlay" onClick={() => setMessage(null)}>
          <div className="learning-message-box" onClick={(e) => e.stopPropagation()}>
            <p className="learning-message-text">{message}</p>
            <button type="button" className="learning-message-ok" onClick={() => setMessage(null)}>OK</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningCertificationPage;

